import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

import { getBackendUrl } from '../services/origin';
const API = getBackendUrl();

const DEFAULT_BRANDING = {
  logo_url: null,
  primary_color: '#D44E20',
  secondary_color: '#3ECF8E',
  background_color: '#111114',
  text_color: '#FFFFFF',
  text_secondary_color: '#888888',
  business_name: '',  // Empty default — never show "LoyaltyPOS" or "Lazo" to a customer before the brand API resolves; show nothing instead.
  tier_names: { bronze: 'Bronze', silver: 'Silver', gold: 'Gold', platinum: 'Platinum' },
  tier_colors: { bronze: '#CD7F32', silver: '#C0C0C0', gold: '#FFD700', platinum: '#888888' },
};

const BrandingContext = createContext(DEFAULT_BRANDING);

function applyBrandCSS(branding) {
  const root = document.documentElement;
  root.style.setProperty('--brand-primary', branding.primary_color || '#D44E20');
  root.style.setProperty('--brand-secondary', branding.secondary_color || '#3ECF8E');
  root.style.setProperty('--brand-bg', branding.background_color || '#111114');
  root.style.setProperty('--brand-text', branding.text_color || '#FFFFFF');
  root.style.setProperty('--brand-text-secondary', branding.text_secondary_color || '#888888');
  // Extra accent palette → exposed as --brand-extra-1..n for stylesheets/components
  (branding.extra_colors || []).forEach((c, idx) => {
    root.style.setProperty(`--brand-extra-${idx + 1}`, c.value || '#000');
  });
  // Reset any leftover slot beyond the current count (max 10)
  for (let i = (branding.extra_colors || []).length + 1; i <= 10; i++) {
    root.style.removeProperty(`--brand-extra-${i}`);
  }
  // Custom fonts
  applyBrandFonts(branding.fonts || {});
}

function applyBrandFonts(fonts) {
  const root = document.documentElement;
  const STYLE_ID = 'brand-fonts-style';
  const head = document.head;
  // Resolve existing dynamic <style> tag we manage
  let styleEl = document.getElementById(STYLE_ID);
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = STYLE_ID;
    head.appendChild(styleEl);
  }
  // Build @font-face rules for uploaded fonts and Google Fonts <link> tags
  const faceRules = [];
  const googleFamilies = new Set();
  ['title', 'subtitle', 'body'].forEach(role => {
    const f = fonts[role];
    if (!f) return;
    if (f.source === 'upload' && f.url && f.name) {
      faceRules.push(`@font-face{font-family:"${f.name}";src:url("${f.url}");font-display:swap;font-weight:${f.weight || 400};}`);
    } else if (f.source === 'google' && f.name) {
      googleFamilies.add(`${f.name}:wght@300;400;500;600;700;800`);
    }
  });
  styleEl.innerHTML = faceRules.join('\n');

  // Google Fonts <link>
  const linkId = 'brand-google-fonts';
  let linkEl = document.getElementById(linkId);
  if (googleFamilies.size > 0) {
    const families = [...googleFamilies].map(f => `family=${encodeURIComponent(f)}`).join('&');
    const href = `https://fonts.googleapis.com/css2?${families}&display=swap`;
    if (!linkEl) {
      linkEl = document.createElement('link');
      linkEl.id = linkId; linkEl.rel = 'stylesheet';
      head.appendChild(linkEl);
    }
    if (linkEl.href !== href) linkEl.href = href;
  } else if (linkEl) {
    linkEl.remove();
  }

  // Expose via CSS vars
  root.style.setProperty('--brand-font-title', fonts.title?.name ? `"${fonts.title.name}", DM Sans, sans-serif` : 'DM Sans, sans-serif');
  root.style.setProperty('--brand-font-subtitle', fonts.subtitle?.name ? `"${fonts.subtitle.name}", DM Sans, sans-serif` : 'DM Sans, sans-serif');
  root.style.setProperty('--brand-font-body', fonts.body?.name ? `"${fonts.body.name}", DM Sans, sans-serif` : 'DM Sans, sans-serif');
  root.style.setProperty('--brand-font-title-weight', fonts.title?.weight || 700);
  root.style.setProperty('--brand-font-subtitle-weight', fonts.subtitle?.weight || 500);
  root.style.setProperty('--brand-font-body-weight', fonts.body?.weight || 400);
}

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(DEFAULT_BRANDING);

  const loadBranding = useCallback(async () => {
    try {
      // Try authenticated first (staff/admin), fallback to public
      const token = localStorage.getItem('staff_token') || localStorage.getItem('admin_token');
      let res;
      if (token) {
        res = await axios.get(`${API}/api/business/branding`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        res = await axios.get(`${API}/api/public/branding`);
      }
      const data = { ...DEFAULT_BRANDING, ...res.data };
      setBranding(data);
      applyBrandCSS(data);
    } catch {
      applyBrandCSS(DEFAULT_BRANDING);
    }
  }, []);

  useEffect(() => {
    loadBranding();
  }, [loadBranding]);

  // Listen for branding updates from admin
  const updateBranding = useCallback((newBranding) => {
    const data = { ...branding, ...newBranding };
    setBranding(data);
    applyBrandCSS(data);
  }, [branding]);

  return (
    <BrandingContext.Provider value={{ ...branding, updateBranding, reloadBranding: loadBranding }}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}
